import React, { useEffect, useState } from 'react';
import { FormDialog, InputField, MuiBox, SelectDropdown, Typography } from '@kis-technologies/kis-ui';
import { useDecimalField } from 'hooks/useDecimalField';
import { VenueTaxData, VenueTaxFormProps } from '../types';
import { useStyles } from '../styled';
import { getSortedList } from 'utils/stringFormat';

export const VenueTaxOverlay = ({ mode, initialData, onClose, onSave, venues, disabled }: VenueTaxFormProps) => {
  const classes = useStyles();

  const [formData, setFormData] = useState<VenueTaxData>({
    venueId: null,
    label: '',
    percentage: null
  });

  const [errors, setErrors] = useState({
    venueId: '',
    label: '',
    percentage: ''
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData(initialData);
    }
  }, [mode, initialData]);

  const validate = () => {
    const newErrors = {
      venueId: '',
      label: '',
      percentage: ''
    };

    if (!formData.venueId) {
      newErrors.venueId = 'Venue is required';
    }
    if (!formData.label.trim()) {
      newErrors.label = 'Tax label is required';
    }
    if (formData.percentage === null) {
      newErrors.percentage = 'Percentage is required';
    }
    setErrors(newErrors);
    return !newErrors.venueId && !newErrors.label && !newErrors.percentage;
  };

  const handleSave = () => {
    const isValid = validate();
    if (isValid) {
      onSave(formData);
    }
  };

  const percentageField = useDecimalField({
    value: formData.percentage,
    onChange: (value) => {
      setFormData((prev) => ({ ...prev, percentage: value }));
      setErrors((prev) => ({ ...prev, percentage: '' }));
    },
    min: 0,
    max: 100
  });

  const sortedVenues = getSortedList(venues, 'name');

  return (
    <FormDialog
      open
      title={mode === 'edit' ? 'Edit Venue Tax' : 'Create Venue Tax'}
      confirmButtonAction={handleSave}
      confirmButtonText="Save"
      confirmButtonDisabled={disabled}
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
    >
      <MuiBox mt="10px">
        <MuiBox className={classes.formBox}>
          <Typography className={classes.formLabel}>Venue</Typography>
          <SelectDropdown
            items={sortedVenues}
            itemToString={(item) => item?.name || ''}
            selectedItem={sortedVenues.find((v) => v.id === formData.venueId) ?? null} // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(item: { id: string; name: string } | null) => {
              setFormData((prev) => ({
                ...prev,
                venueId: item?.id ?? null
              }));
              setErrors((prev) => ({
                ...prev,
                venueId: ''
              }));
            }}
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: formData.venueId ? '#000' : '#9E9E9E'
              },
              border: errors.venueId ? '1px solid red' : ''
            }}
            disabled={mode === 'edit' || disabled}
          />
          {errors.venueId && <Typography color="red">{errors.venueId}</Typography>}
        </MuiBox>
        <MuiBox className={classes.formBox}>
          <InputField
            label="Tax Label"
            value={formData.label}
            error={!!errors.label}
            helperText={errors.label}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                label: e.target.value
              }));
              setErrors((prev) => ({
                ...prev,
                label: ''
              }));
            }}
            sx={{
              width: '262px',
              height: '45px'
            }}
            disabled={disabled}
          />
        </MuiBox>
        <MuiBox className={classes.formBox}>
          <InputField
            label="Tax Percentage"
            type="text"
            align="right"
            error={!!errors.percentage}
            helperText={errors.percentage}
            value={percentageField.displayedValue}
            onFocus={percentageField.handleFocus}
            onChange={(e) => percentageField.handleChange(e.target.value)}
            onBlur={percentageField.handleBlur}
            size="small"
            endIcon="%"
            inputProps={{ inputMode: 'decimal' }}
            sx={{
              width: { xs: '262px', sm: '175px' },
              height: '45px'
            }}
            disabled={disabled}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};
